import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { LIGHT_THEME, renderCssVariables, renderEffectsCss } from '@vello/tokens';
import { App } from './App';
import { loadRuntimeConfig, resolveApiBaseUrl, resolveGeoCountry, type SiteRuntimeConfig } from './config';
import { widgetScriptAttributes } from '../src/widget-embed';
import { publicRouteOf } from '../src/public-pages';
import { PublicPage } from './PublicPage';
import { SiteErrorBoundary } from './SiteErrorBoundary';
import './fonts.css';
import './theme.css';

/** Inject the Vello light theme as CSS custom properties on `:root` before first render (no color literals).
 *  The institutional site follows the authoritative Vello Claude Design (Site Institucional.dc.html): a light
 *  surface — white page, mist sections, ink navy text + primary buttons, an ink navy footer. */
function installTheme(): void {
  const style = document.createElement('style');
  style.id = 'vello-theme';
  style.textContent = `:root {\n${renderCssVariables(LIGHT_THEME)}\n}`;
  document.head.appendChild(style);
  // The shared effect/microinteraction layer (Claude Design "Modernização com efeitos"), reduced-motion safe.
  const fx = document.createElement('style');
  fx.id = 'vello-effects';
  fx.textContent = renderEffectsCss();
  document.head.appendChild(fx);
}

/**
 * Install the Central widget (D427) the way any partner site does — one async `<script src="/widget.js" data-key>` — when
 * the runtime config carries the Vello-site key. The widget renders in its own shadow root and never restyles the site.
 */
function installCentralWidget(config: SiteRuntimeConfig): void {
  const attrs = widgetScriptAttributes(config.widget);
  if (attrs === undefined) return;
  const script = document.createElement('script');
  script.async = true;
  for (const [name, value] of Object.entries(attrs)) if (value !== undefined) script.setAttribute(name, value);
  document.body.appendChild(script);
}

async function boot(): Promise<void> {
  installTheme();
  // D440: a trip-share link (`/v/<token>`) or a legal text (`/legal/…`) is a public page of its own — no market
  // resolution, no widget, no geo lookup.
  const route = typeof location !== 'undefined' ? publicRouteOf(location.pathname) : undefined;
  if (route !== undefined) {
    const cfg = await loadRuntimeConfig();
    const mount = document.getElementById('root');
    if (mount === null) throw new Error('Vello site: missing #root mount node');
    createRoot(mount).render(
      <StrictMode>
        <SiteErrorBoundary>
          <PublicPage route={route} apiOrigin={resolveApiBaseUrl(cfg)} />
        </SiteErrorBoundary>
      </StrictMode>,
    );
    return;
  }
  // Resolve the served-country registry + per-market content before first paint (same-origin /site-config.json).
  const config = await loadRuntimeConfig();
  // Real IP-geo: resolve the visitor's actual country and feed it as the geo hint (the edge can't inject
  // `cf-ipcountry` with the Cloudflare proxy off). Fail-soft — a null result keeps the fallback market, so the
  // redirect never blocks on this and never regresses. Only overrides when a real 2-letter country came back.
  const geoCountry = await resolveGeoCountry(config);
  const resolvedConfig = geoCountry !== null ? { ...config, geoCountryHeader: geoCountry } : config;
  const container = document.getElementById('root');
  if (container === null) throw new Error('Vello site: missing #root mount node');
  createRoot(container).render(
    <StrictMode>
      {/* D442 (X-08): a crashed page shows a sentence and a reload, never a blank page. */}
      <SiteErrorBoundary>
        <App config={resolvedConfig} />
      </SiteErrorBoundary>
    </StrictMode>,
  );
  installCentralWidget(config);
}

void boot();
